import { useEffect, useState, useCallback } from "react";
import { apiGet } from "../api/http";
import { useAuth } from "../context/AuthContext";
import AuthCallout from "../components/AuthCallout";
import { isAuthError, parseApiError } from "../utils/apiErrors";
import DataTable from "../components/shared/DataTable";

const KeywordResearch = () => {
  const { isAuthenticated } = useAuth();
  const [keyword, setKeyword] = useState("");
  const [keywordsData, setKeywordsData] = useState([]);
  const [status, setStatus] = useState({ provider: "", available: false, hint: "", comingSoon: false });
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    if (!isAuthenticated) {
      setLoadingStatus(false);
      return;
    }
    let active = true;
    async function loadStatus() {
      setLoadingStatus(true);
      try {
        const { data } = await apiGet("/api/seo/keywords/status");
        if (active) setStatus(data);
      } catch (err) {
        if (active) setApiError(parseApiError(err, "Failed to load keyword feature status"));
      } finally {
        if (active) setLoadingStatus(false);
      }
    }
    loadStatus();
    return () => { active = false; };
  }, [isAuthenticated]);

  // Stable reference so onKeyDown doesn't re-bind on every render
  const fetchKeywords = useCallback(async () => {
    const sanitized = keyword.trim();
    if (!sanitized || !isAuthenticated || !status.available) return;

    setLoading(true);
    setApiError("");

    try {
      const { data } = await apiGet("/api/seo/keywords", { keyword: sanitized });

      // Batch updates to reduce render cycles
      setKeywordsData(Array.isArray(data.keywords) ? data.keywords : []);
      setStatus((prev) => ({
        ...prev,
        provider: data.provider || prev.provider,
        hint: data.hint || prev.hint,
        available: data.available ?? prev.available,
      }));
    } catch (err) {
      const message = parseApiError(err, "Failed to fetch keyword data");
      setApiError(isAuthError(err) ? `${message}. Please sign in.` : message);
    } finally {
      setLoading(false);
    }
  }, [keyword, isAuthenticated, status.available]);

  const featureUnavailable = isAuthenticated && !loadingStatus && !status.available && !status.comingSoon;

  // Detect whether we have richer DataForSEO data (source field present)
  const hasSource = keywordsData.some((kw) => kw.source != null);
  const hasTrend = keywordsData.some((kw) => kw.trend?.length > 0);

  // Coming soon state — backend stub returns comingSoon: true when no provider is configured
  if (!loadingStatus && status.comingSoon) {
    return (
      <div className="page">
        <h1 className="page-title">Keyword Research</h1>
        <p className="dashboard-lead" style={{ marginTop: "-8px" }}>
          Deep keyword intelligence — search volume, CPC, competition scores, and trend analysis.
        </p>

        <div
          className="card"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "64px 32px",
            textAlign: "center",
            minHeight: 340,
            background: "linear-gradient(135deg, var(--bg-card) 0%, var(--accent-subtle) 100%)",
            border: "1.5px dashed var(--accent)",
            borderRadius: "var(--radius)",
            gap: 20,
          }}
        >
          <div style={{ fontSize: "3rem", lineHeight: 1 }}>🔬</div>

          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "var(--accent)",
              color: "#fff",
              borderRadius: 20,
              padding: "4px 14px",
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Coming Soon
          </div>

          <h2
            style={{
              margin: 0,
              fontSize: "1.45rem",
              fontWeight: 700,
              color: "var(--text-heading)",
            }}
          >
            Keyword Research is on the way
          </h2>

          <p
            style={{
              maxWidth: 460,
              fontSize: "0.9rem",
              color: "var(--text-muted)",
              margin: 0,
              lineHeight: 1.65,
            }}
          >
            {status.hint}
          </p>

          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              justifyContent: "center",
              marginTop: 8,
            }}
          >
            {["Search Volume", "Keyword Difficulty", "CPC Data", "Trend Analysis", "Related Keywords"].map(
              (label) => (
                <span
                  key={label}
                  style={{
                    background: "var(--accent-subtle)",
                    color: "var(--accent-hover)",
                    borderRadius: 20,
                    padding: "4px 14px",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                  }}
                >
                  {label}
                </span>
              )
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <h1 className="page-title">Keyword Research</h1>
      <p className="dashboard-lead" style={{ marginTop: "-8px" }}>
        {status.provider === "dataforseo"
          ? "Live search volume, competition, and CPC data powered by DataForSEO. Includes related keyword suggestions."
          : "Beta keeps this page honest: it only shows live data when a provider is configured."}
      </p>

      <AuthCallout feature="keyword research" />

      <div className="card">
        <div className="input-group">
          <input
            className="input"
            type="text"
            placeholder="Enter a keyword (e.g. seo tools)"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchKeywords()}
            disabled={!isAuthenticated}
          />
          <button
            className="btn btn-primary"
            onClick={fetchKeywords}
            disabled={!isAuthenticated || !status.available || loading || !keyword.trim()}
          >
            {loading ? "Searching..." : "Find Keywords"}
          </button>
        </div>

        {loadingStatus ? (
          <div className="loading">Checking keyword provider status...</div>
        ) : null}

        {status.hint ? (
          <div className={featureUnavailable ? "auth-callout compact" : "hint"}>
            <strong>{status.provider || "keywords"}:</strong> {status.hint}
          </div>
        ) : null}

        {apiError ? <div className="progress-error">{apiError}</div> : null}

        {featureUnavailable ? (
          <div className="site-panel-empty" style={{ marginTop: 12 }}>
            Live keyword data is unavailable in this environment.
            Set <code>KEYWORD_PROVIDER=dataforseo</code> and add your DataForSEO credentials to enable it.
          </div>
        ) : null}

        {keywordsData.length > 0 ? (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: 8 }}>
              {keywordsData.length} keyword{keywordsData.length !== 1 ? "s" : ""} found
              {hasSource && (
                <span style={{ marginLeft: 8 }}>
                  — <span style={{ color: "var(--accent)", fontWeight: 600 }}>Exact</span> match + related suggestions
                </span>
              )}
            </div>
            
            <DataTable
              headers={[
                { key: "keyword", label: "Keyword", render: (kw) => <strong style={{ color: "var(--text-heading)" }}>{kw.keyword}</strong> },
                { key: "searchVolume", label: "Search Volume", render: (kw) => kw.searchVolume != null ? kw.searchVolume.toLocaleString() : "—" },
                { key: "competition", label: "Competition", render: (kw) => <CompetitionBadge value={kw.competition} /> },
                { key: "avgCpc", label: "Avg CPC", render: (kw) => kw.avgCpc != null ? `$${kw.avgCpc.toFixed(2)}` : "—" },
                ...(hasTrend ? [{ key: "trend", label: "6-Month Trend", render: (kw) => kw.trend?.length > 0 ? <TrendSparkline data={kw.trend} /> : <span style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>—</span> }] : []),
                ...(hasSource ? [{ key: "source", label: "Type", render: (kw) => (
                  <span
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      padding: "2px 8px",
                      borderRadius: 10,
                      background: kw.source === "exact" ? "rgba(99,102,241,0.1)" : "rgba(100,116,139,0.1)",
                      color: kw.source === "exact" ? "var(--accent)" : "var(--text-muted)",
                    }}
                  >
                    {kw.source === "exact" ? "Exact" : "Related"}
                  </span>
                ) }] : [])
              ]}
              data={keywordsData}
              keyExtractor={(kw, i) => `${kw.keyword}-${i}`}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Small coloured badge for competition level.
 * Accepts number | string | null from both Google Ads and DataForSEO providers.
 * Numeric competition values (0–1 float from DataForSEO) are bucketed into
 * Low / Medium / High labels before rendering.
 */
function CompetitionBadge({ value }) {
  // Treat null, undefined, and the legacy "N/A" string the same way
  if (value == null || value === "N/A") {
    return <span style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>—</span>;
  }

  // DataForSEO returns competition as a 0–1 float; bucket into labels
  let label = value;
  if (typeof value === "number") {
    if (value < 0.33) label = "Low";
    else if (value < 0.67) label = "Medium";
    else label = "High";
  }

  const colors = {
    Low: { bg: "rgba(22,163,74,0.1)", text: "var(--success)" },
    Medium: { bg: "rgba(217,119,6,0.1)", text: "var(--warning)" },
    High: { bg: "rgba(220,38,38,0.1)", text: "var(--danger)" },
  };
  const c = colors[label] || { bg: "rgba(100,116,139,0.1)", text: "var(--text-muted)" };

  return (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: 10,
        fontSize: "0.75rem",
        fontWeight: 700,
        background: c.bg,
        color: c.text,
      }}
    >
      {label}
    </span>
  );
}

/**
 * Tiny inline SVG sparkline showing 6-month search volume trend.
 * Guards against flat data (min === max) so the line still renders centred.
 */
function TrendSparkline({ data }) {
  if (!data || data.length < 2) return null;

  const W = 80;
  const H = 24;
  const values = data.map((d) => d.volume);
  const min = Math.min(...values);
  const max = Math.max(...values);
  // Avoid division by zero when all values are identical (flat keyword trend)
  const range = max - min || 1;

  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * W;
    // Centre the line vertically when the range is 0 (flat trend)
    const y = range === 1 && max === min
      ? H / 2
      : H - ((v - min) / range) * (H - 4) - 2;
    return `${x},${y}`;
  });

  const lastVal = values[values.length - 1];
  const firstVal = values[0];
  const trending = lastVal >= firstVal;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <svg width={W} height={H} style={{ overflow: "visible" }}>
        <polyline
          points={points.join(" ")}
          fill="none"
          stroke={trending ? "var(--success)" : "var(--danger)"}
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
      <span
        style={{
          fontSize: "0.7rem",
          color: trending ? "var(--success)" : "var(--danger)",
          fontWeight: 700,
        }}
      >
        {trending ? "▲" : "▼"}
      </span>
    </div>
  );
}

export default KeywordResearch;